import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InvestmentPlaceholder } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentPlaceholder/InvestmentPlaceholder.tsx';
import { Badge, Flex, Text } from '@mantine/core';
import { CardBodySkeleton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/CardBodySkeleton.tsx';
import { InvestmentDetailsRowItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/components/InvestmentDetailsRowItem/InvestmentDetailsRowItem.tsx';
import { useInvestmentDetailsBankInformation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsBankInformation.ts';
import { ManageRequirementsButton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/ManageRequirementsButton.tsx';
import { getBankInformationRows } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentBankInformation/InvestmentBankInformation.utils.ts';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';

interface Props {
	onManageRequirements: () => void;
	canMutate: boolean;
}

export const InvestmentBankInformation = ({ onManageRequirements, canMutate }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { data, isLoading } = useInvestmentDetailsBankInformation();
	const bankInformation = data?.bankInformation ?? null;

	const rows = bankInformation
		? getBankInformationRows(bankInformation, {
				accountHolder: tMipManage('investment_bank_information.row.account_holder'),
				bankName: tMipManage('investment_bank_information.row.bank_name'),
				accountNumber: tMipManage('investment_bank_information.row.account_number'),
				swiftCode: tMipManage('investment_bank_information.row.swift_code'),
				iban: tMipManage('investment_bank_information.row.iban'),
				country: tMipManage('investment_bank_information.row.country'),
				currency: tMipManage('investment_bank_information.row.currency'),
			})
		: null;

	const renderBody = () => {
		if (isLoading) {
			return <CardBodySkeleton count={5} mih={151} />;
		}

		if (rows?.length) {
			return (
				<Flex direction="column" p="0.5rem" className={REPLAY_MASK_CLASS} data-sentry-mask>
					{rows.map(({ label, value }, index) => (
						<InvestmentDetailsRowItem
							key={label}
							label={<Text c="dark.0">{label}</Text>}
							content={
								<Text fw={600} c="dark.8">
									{value}
								</Text>
							}
							hideBorder={index === 0}
						/>
					))}
				</Flex>
			);
		}

		return (
			<InvestmentPlaceholder
				icon="building-office-company-entity-line"
				label={tMipManage('investment_bank_information.placeholder_label')}
				mih={151}
			/>
		);
	};

	return (
		<InvestmentDetailsCard
			cardLabel={tMipManage('investment_bank_information.card_label')}
			labelAfter={
				bankInformation?.isNew ? (
					<Badge
						variant="outline"
						size="sm"
						mih="1.5rem"
						h="1.5rem"
						radius="999px"
						p="0.25rem 0.5rem"
						color="dark.4"
					>
						<Text fz="sm" fw={400} c="dark.4">
							{tMipManage('investment_bank_information.new_badge')}
						</Text>
					</Badge>
				) : null
			}
			actions={
				<ManageRequirementsButton canMutate={canMutate} onClick={onManageRequirements}>
					{tMipManage('investment_bank_information.button.manage_requirements')}
				</ManageRequirementsButton>
			}
		>
			{renderBody()}
		</InvestmentDetailsCard>
	);
};
